import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import BillingInvoiceRecipientFormSection from "modules/project/components/forms/formSections/InvoiceRecipientFormSection";
import type React from "react";
import { useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button.tsx";
import ComboBox from "~/components/formElements/ComboBox";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import RadioGroup from "~/components/formElements/RadioGroup";
import Select from "~/components/formElements/Select";
import SubmitButton from "~/components/formElements/SubmitButton";
import BudgetFormSection from "~/components/formSections/BudgetFormSection";
import CommentFormSection from "~/components/formSections/CommentFormSection";
import PlaceOfPerformanceFormSection from "~/components/formSections/PlaceOfPerformanceFormSection";
import TimingFormSection from "~/components/formSections/TimingFormSection";
import TitleFormSection from "~/components/formSections/TitleFormSection";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { DEFAULT_COUNTRY_ID } from "~/constants/form.ts";
import { useAuth } from "~/contexts/AuthContext";
import useApprovalLikelihoodOptions from "~/hooks/form/formOptionsData/useApprovalLikelihoodOptions.ts";
import useClientsContactPersonsSelectOptions
	from "~/hooks/form/formOptionsData/useClientsContactPersonsSelectOptions.tsx";
import useClientsInvoiceRecipientSelectOptions
	from "~/hooks/form/formOptionsData/useClientsInvoiceRecipientsSelectOptions.ts";
import useCountrySelectOptions from "~/hooks/form/formOptionsData/useCountrySelectOptions.ts";
import useLocationOptions from "~/hooks/form/formOptionsData/useLocationOptions.ts";
import useUserSelectOptions, { allActiveUsersFilterFn } from "~/hooks/form/formOptionsData/useUsersSelectOptions.ts";
import useWorkingStatusOptions from "~/hooks/form/formOptionsData/useWorkingStatusOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	useProjectPhaseTitleValidationComparisonData,
} from "~/hooks/form/validationComparisonData/useProjectPhaseTitleValidationComparisonData.ts";
import { createProjectPhase } from "~/modules/project/api/projectPhase/projectPhaseApiDispatchers.ts";
import type { CreateProjectPhaseData } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import { WorkingStatusId } from "~/modules/project/api/workingStatus/workingStatusTypes.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter, transformEmptyDateValueToNull } from "~/utils/form/formUtils.ts";

type CreateProjectPhaseFormProps = {
	clientId: string;
	projectId: string;
	onSuccess: (newPhaseNumber: number) => void;
	onCancel: () => void;
};

type CreateProjectPhaseFormData = Omit<CreateProjectPhaseData, "startDate" | "endDate"> & {
	startDate: Date;
	endDate: Date;
}

const userFilterOptions = { filterFunction: allActiveUsersFilterFn };
const CreateProjectPhaseForm: React.FC<CreateProjectPhaseFormProps> = ({
	clientId,
	projectId,
	onSuccess,
	onCancel,
}) => {
	//const { t } = useTranslation();
	const [busy, setBusy] = useState(false);
	const { user } = useAuth();
	const titleComparisonData = useProjectPhaseTitleValidationComparisonData(projectId);

	const schema = useMemo(() => {
		return yup.object({
			approvalLikelihoodId: yup.string().required(),
			budgetCentsPlanned: yup.number().required(),
			clientContactPersonId: yup.string().nullable().default(null),
			comment: yup.string().default(null),
			countryId: yup.string().required(),
			endDate: yup
				.date()
				.transform(transformEmptyDateValueToNull)
				.nullable()
				.required()
				.min(yup.ref("startDate"), "Das Startdatum muss vor dem Enddatum liegen."),
			invoiceRecipientId: yup.string().nullable().default(null),
			locationName: yup.string().required(),
			manDaysPlanned: yup.number().typeError(t("form:validationErrors.number.requiredTypeError")).required(),
			phaseManagedBy: yup.string().default(null),
			phaseLeadBy: yup.string().nullable().default(null),
			projectId: yup.string().required(),
			startDate: yup.date().transform(transformEmptyDateValueToNull).nullable().required(),
			title: yup.string().required().unique(titleComparisonData),
			workingStatusId: yup.string().required(),
		}).test("atLeastOneField", "Bitte wähle einen Kontakt oder zentralen Rechnungsempfänger aus.", (value) =>

			Boolean(value.clientContactPersonId || value.invoiceRecipientId),
		);
	}, [titleComparisonData]);

	const defaultValues = useMemo(() => {
		return {
			approvalLikelihoodId: "1",
			budgetCentsPlanned: 0,
			clientContactPersonId: null,
			comment: "",
			countryId: DEFAULT_COUNTRY_ID,
			endDate: "" as unknown as Date,
			invoiceRecipientId: null,
			locationName: "",
			manDaysPlanned: 0,
			projectId: projectId || "",
			phaseManagedBy: user?.id,
			phaseLeadBy: null,
			startDate: "" as unknown as Date,
			title: "",
			workingStatusId: "1",
		};
	}, [user, projectId]);

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty, isSubmitted, isValid },
		setValue,
		trigger,
		watch,
	} = useForm<CreateProjectPhaseFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<CreateProjectPhaseFormData>(schema),
	});
	const approvalLikelihoodOptions = useApprovalLikelihoodOptions();
	const countrySelectOptions = useCountrySelectOptions();
	const managerSelectOptions = useUserSelectOptions(userFilterOptions);
	const locationOptions = useLocationOptions();
	const workingStatusOptions = useWorkingStatusOptions();
	const clientContactPersonSelectOptions = useClientsContactPersonsSelectOptions(clientId);
	const clientsInvoiceRecipientSelectOptions = useClientsInvoiceRecipientSelectOptions(clientId);

	const formIsSubmittable = useFormIsSubmittable({ isDirty, isSubmitted, isValid, isLoading: busy });

	const onSubmit: SubmitHandler<CreateProjectPhaseFormData> = async (data: CreateProjectPhaseFormData) => {
		setBusy(true);
		if (data.workingStatusId !== WorkingStatusId.Quotation) {
			data.approvalLikelihoodId = null;
		}

		const phaseData = {
			...data,
			startDate: formatDateToYYYYMMDD(data.startDate),
			endDate: formatDateToYYYYMMDD(data.endDate),
		};

		try {
			const res = await createProjectPhase(projectId || data.projectId, phaseData);
			const newProjectPhaseNumber = getDataFromResponse(res).phaseNumber;
			onSuccess(newProjectPhaseNumber);
		} catch (error) {
			console.log(error);
		}
	};

	const showApprovalLikelihoodInput = watch("workingStatusId") === "1";

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Neue Projektphase erstellen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				<FormSectionGroup>
					<TitleFormSection control={control}
									  title="Name" />

					<FormSection title="Status">
						<div className="grid grid-cols-6 gap-x-6 gap-y-2">
							<div className="col-span-6">
								<Select control={control}
										name="workingStatusId"
										optionsData={workingStatusOptions} />
							</div>
							{showApprovalLikelihoodInput && (
								<div className="col-span-6">
									<RadioGroup
										control={control}
										name="approvalLikelihoodId"
										options={approvalLikelihoodOptions}
										label="Bewertung"
									/>
								</div>
							)}
						</div>
					</FormSection>
					<BudgetFormSection control={control}
									   watch={watch}
									   title="Plandaten"
									   errors={errors} />

					<TimingFormSection control={control}
									   watch={watch}
									   setValue={setValue}
									   errors={errors}
									   title="Laufzeit" />
					<PlaceOfPerformanceFormSection
						errors={errors}
						control={control}
						locationOptions={locationOptions}
						countrySelectOptions={countrySelectOptions}
					/>

					<FormSection title="Verantwortliche/r der Phase ">
						<div className="grid grid-cols-6 gap-x-6 gap-y-2">
							<div className="col-span-6">
								<ComboBox
									allowNew={false}
									name="phaseManagedBy"
									label={"Angebot Manager"}
									error={errors.phaseManagedBy?.message}
									optionsData={managerSelectOptions}
									control={control}
								/>
							</div>
							<div className="col-span-6">
								<ComboBox
									allowNew={false}
									name="phaseLeadBy"
									label={"Projektleitung"}
									error={errors.phaseManagedBy?.message}
									optionsData={managerSelectOptions}
									control={control}
								/>
							</div>
						</div>
					</FormSection>
					<FormSection title="Rechnungsempfänger">
						<BillingInvoiceRecipientFormSection
							control={control}
							clientContactPersonSelectOptions={clientContactPersonSelectOptions}
							// @ts-expect-error - needs to be fixed
							errorMessage={errors[""]?.message}
							invoiceRecipientSelectOptions={clientsInvoiceRecipientSelectOptions}
							setValue={setValue}
							trigger={trigger}
							watch={watch}
						/>
					</FormSection>
					<CommentFormSection
						control={control}
						placeholder={"Hier können allgemeine Anmerkungen zur Projektphase hinterlegt werden."}
						label="Kommentar"
						title="Anmerkungen"
					/>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					Speichern
				</SubmitButton>
				<Button theme="none"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default CreateProjectPhaseForm;
